<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSidebarState } from '@/composables/useSidebarState'
import { confirmDiscardDraft, installDraftGuards } from '@/composables/useDirtyDraftGuard'
import { useSentryFeedback } from '@/composables/useSentryFeedback'
import { useRecoveryNotification } from '@/composables/useRecoveryNotification'
import { useRecordingUpload } from '@/composables/useRecordingUpload'
import { useAnnouncer } from '@/composables/useAnnouncer'
import { getAppMode } from '@/utils/domain'
import SideMenu from '@/components/organisms/SideMenu.vue'
import TopBar from '@/components/organisms/TopBar.vue'
import ContextSectionStrip from '@/components/organisms/ContextSectionStrip.vue'
import ImpersonationBanner from '@/components/organisms/ImpersonationBanner.vue'
import ReadOnlyBanner from '@/components/organisms/ReadOnlyBanner.vue'
import RecoveryBanner from '@/components/recording/RecoveryBanner.vue'
import RecoveryDialog from '@/components/recording/RecoveryDialog.vue'
import AgreementReacceptanceDialog from '@/components/organisms/AgreementReacceptanceDialog.vue'
import SkipLink from '@/components/atoms/SkipLink.vue'
import ToastContainer from '@/components/organisms/ToastContainer.vue'

const authStore = useAuthStore()
const router = useRouter()

// App-wide unsaved-work guard (#3297): flushes autosaveable drafts on
// navigation, asks before dropping anything that could not be saved.
installDraftGuards(router, confirmDiscardDraft)
const { message: announcerMessage, politeness: announcerPoliteness } = useAnnouncer()

// Set up Sentry feedback widget language reactivity
useSentryFeedback()
const route = useRoute()
const isMobileMenuOpen = ref(false)
const { isCollapsed: isSidebarCollapsed } = useSidebarState()

// Session recovery notification (checks on mount, non-blocking)
const {
  showRecoveryDialog,
  recoverableSessions,
  isLoading: isRecoveryLoading,
  openRecoveryDialog,
  closeRecoveryDialog,
  discardSession,
} = useRecoveryNotification()

// Track the appointment ID for the session being uploaded (for navigation after success)
const uploadingAppointmentId = ref<string | null>(null)

// Recording upload for recovered sessions
const recoveryUpload = useRecordingUpload({
  onSuccess: () => {
    // Navigate to the appointment detail page where the transcript is shown
    if (uploadingAppointmentId.value) {
      router.push({ name: 'appointment-detail', params: { id: uploadingAppointmentId.value } })
    }
    uploadingAppointmentId.value = null
    closeRecoveryDialog()
  },
  onError: () => {
    uploadingAppointmentId.value = null
  },
})

const isBannerDismissed = ref(false)

// Show navigation only when authenticated, not on login pages, and not on client/admin portal
// The client portal has its own PortalLayout with PortalHeader
// The admin portal has its own AdminLayout with AdminSidebar
// Exclude any route with meta.requiresAuth: false (login, admin-login, etc.)
// Note: Call getAppMode() directly instead of using pre-computed constant to avoid timing issues
const showNavigation = computed(() => {
  const appMode = getAppMode()
  return authStore.isAuthenticated && route.meta.requiresAuth !== false && appMode === 'behandelaar'
})

// Show recovery banner only when authenticated and on main app (not client portal)
const showRecoveryBanner = computed(() => {
  return showNavigation.value && recoverableSessions.value.length > 0 && !isBannerDismissed.value
})

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false
}

function handleViewRecovery() {
  openRecoveryDialog()
}

function handleDismissBanner() {
  isBannerDismissed.value = true
}

async function handleUploadSession(sessionId: string) {
  // Find the session in recoverable sessions
  const recoverable = recoverableSessions.value.find((r) => r.session.id === sessionId)
  if (!recoverable) {
    console.error('Session not found:', sessionId)
    return
  }

  const session = recoverable.session

  // A recording is owned by exactly one of an appointment or a form submission.
  // Sessions with neither owner cannot be linked to a transcript and are not
  // uploadable.
  if (!session.appointmentId && !session.formSubmissionId) {
    console.warn('Cannot upload session without an appointment or form owner:', sessionId)
    return
  }

  // Store the appointment ID (if any) for navigation after a successful upload.
  // Form-owned sessions have no appointment detail page to navigate to.
  uploadingAppointmentId.value = session.appointmentId ?? null

  // Use the recording upload composable to handle the full upload flow.
  // The persisted speakerCount hint travels with the session.
  const success = await recoveryUpload.upload(session)
  if (success) {
    // Session data is cleaned up by useRecordingUpload on success
    // Just remove from recoverable sessions list
    discardSession(sessionId)
  }
}

function handleResumeSession(sessionId: string) {
  // Find the session to get the appointment ID for navigation
  const recoverable = recoverableSessions.value.find((r) => r.session.id === sessionId)
  if (!recoverable) {
    console.error('Session not found:', sessionId)
    return
  }

  const session = recoverable.session

  // Navigate to the appointment to resume recording
  if (session.appointmentId) {
    closeRecoveryDialog()
    router.push({ name: 'appointment-detail', params: { id: session.appointmentId } })
  } else {
    // Session without appointment - can't navigate to resume
    console.warn('Cannot resume session without appointment:', sessionId)
    closeRecoveryDialog()
  }
}

async function handleDiscardSession(sessionId: string) {
  await discardSession(sessionId)
}

// Reset banner dismissed state when sessions change (e.g., after recovery)
watch(
  () => recoverableSessions.value.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) {
      isBannerDismissed.value = false
    }
  }
)
</script>

<template>
  <div class="app">
    <!-- Skip to main content link for keyboard accessibility -->
    <SkipLink v-if="showNavigation" />

    <!-- Impersonation banner (admin viewing as user) -->
    <ImpersonationBanner
      v-if="showNavigation"
      :class="['app__impersonation-banner', { 'app__impersonation-banner--sidebar-collapsed': isSidebarCollapsed }]"
    />

    <!-- Organisation read-only banner (write gate refused a save, #4445) -->
    <ReadOnlyBanner
      v-if="showNavigation"
      :class="['app__read-only-banner', { 'app__read-only-banner--sidebar-collapsed': isSidebarCollapsed }]"
    />

    <!-- Recovery banner (non-blocking notification) -->
    <RecoveryBanner
      v-if="showRecoveryBanner"
      :session-count="recoverableSessions.length"
      :is-dismissed="isBannerDismissed"
      :class="['app__recovery-banner', { 'app__recovery-banner--sidebar-collapsed': isSidebarCollapsed }]"
      @view="handleViewRecovery"
      @dismiss="handleDismissBanner"
    />

    <!-- Top bar -->
    <TopBar
      v-if="showNavigation"
      :show-menu-button="true"
      :menu-open="isMobileMenuOpen"
      @toggle-menu="toggleMobileMenu"
      :class="['app__topbar', { 'app__topbar--sidebar-collapsed': isSidebarCollapsed }]"
    />

    <!-- Overlay for mobile -->
    <div
      v-if="showNavigation && isMobileMenuOpen"
      class="app__overlay"
      @click="closeMobileMenu"
    ></div>

    <!-- Side menu -->
    <SideMenu
      v-if="showNavigation"
      :class="['app__sidebar', { 'app__sidebar--mobile-open': isMobileMenuOpen, 'app__sidebar--collapsed': isSidebarCollapsed }]"
      @navigate="closeMobileMenu"
    />

    <main
      id="main-content"
      tabindex="0"
      class="app__main"
      :class="{ 'app__main--full': !showNavigation, 'app__main--with-topbar': showNavigation, 'app__main--sidebar-collapsed': isSidebarCollapsed && showNavigation }"
    >
      <ContextSectionStrip v-if="showNavigation" />
      <RouterView />
    </main>

    <!-- Recovery dialog (modal) -->
    <RecoveryDialog
      :is-open="showRecoveryDialog"
      :sessions="recoverableSessions"
      :is-loading="isRecoveryLoading"
      :on-upload="handleUploadSession"
      :on-discard="handleDiscardSession"
      @close="closeRecoveryDialog"
      @resume="handleResumeSession"
    />

    <!-- A newly published agreement edition to accept (#4494); dismissible, never blocking -->
    <AgreementReacceptanceDialog v-if="showNavigation" />

    <!-- Global toast queue (teleported to body), shared by all three portals -->
    <ToastContainer />

    <!-- ARIA live region for screen reader announcements -->
    <div
      :aria-live="announcerPoliteness"
      aria-atomic="true"
      class="sr-only"
    >
      {{ announcerMessage }}
    </div>
  </div>
</template>

<style scoped>
.app {
  display: grid;
  /* Banners sit in flow above the TopBar so they push it (and main) down
     instead of overlaying it. Their rows are `auto`, so they collapse to 0
     height when the banner is not rendered. */
  grid-template-rows: auto auto auto auto 1fr;
  /* Explicit 0 minimum stops min-content blowout: wide page content (e.g. a
     1100px table) can never force this column past the viewport width and
     drag the sticky TopBar's right cluster into the clipped zone. */
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'impersonation'
    'read-only'
    'recovery'
    'topbar'
    'main';
  height: 100vh;
  overflow: hidden;
  position: relative;
}

/* Banners are grid rows offset by the sidebar width (like the TopBar), so they
   stack above the TopBar rather than being painted over it. The impersonation
   row is placed above the recovery row, so the two coexist without overlap. */
.app__impersonation-banner {
  grid-area: impersonation;
  margin-left: var(--sidebar-width-expanded);
  transition: margin-left var(--transition-duration-moderate) var(--transition-easing-ease-out);
}

.app__impersonation-banner--sidebar-collapsed {
  margin-left: var(--sidebar-width-collapsed);
}

.app__read-only-banner {
  grid-area: read-only;
  margin-left: var(--sidebar-width-expanded);
  transition: margin-left var(--transition-duration-moderate) var(--transition-easing-ease-out);
}

.app__read-only-banner--sidebar-collapsed {
  margin-left: var(--sidebar-width-collapsed);
}

.app__recovery-banner {
  grid-area: recovery;
  margin-left: var(--sidebar-width-expanded);
  transition: margin-left var(--transition-duration-moderate) var(--transition-easing-ease-out);
}

.app__recovery-banner--sidebar-collapsed {
  margin-left: var(--sidebar-width-collapsed);
}

.app__topbar {
  grid-area: topbar;
  z-index: var(--z-fixed);
  margin-left: var(--sidebar-width-expanded);
  transition: margin-left var(--transition-duration-moderate) var(--transition-easing-ease-out);
}

.app__topbar--sidebar-collapsed {
  margin-left: var(--sidebar-width-collapsed);
}

.app__overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: var(--color-overlay-modal);
  z-index: var(--z-fixed);
}

.app__sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  z-index: calc(var(--z-fixed) + 10);
}

.app__main {
  grid-area: main;
  /* min-width: 0 forces the grid item's automatic minimum to 0 (belt-and-
     suspenders alongside minmax(0, 1fr)); overflow-x: auto makes main a real
     horizontal scroll container so genuinely-wide content scrolls internally
     instead of being clipped by .app / body overflow. */
  min-width: 0;
  overflow-x: auto;
  overflow-y: auto;
  background: var(--color-background-canvas);
  margin-left: var(--sidebar-width-expanded);
  transition: margin-left var(--transition-duration-moderate) var(--transition-easing-ease-out);
  /* Remove focus outline - main landmark shouldn't show visual focus ring */
  outline: none;
}

.app__main--full {
  margin-left: 0;
}

.app__main--sidebar-collapsed {
  margin-left: var(--sidebar-width-collapsed);
}

/* Mobile styles */
@media (--breakpoint-lg-down) {
  .app__impersonation-banner,
  .app__impersonation-banner--sidebar-collapsed,
  .app__read-only-banner,
  .app__read-only-banner--sidebar-collapsed,
  .app__recovery-banner,
  .app__recovery-banner--sidebar-collapsed {
    margin-left: 0;
  }

  .app__overlay {
    display: block;
  }

  .app__sidebar {
    left: calc(-1 * var(--sidebar-width-expanded));
    transition: left var(--transition-duration-moderate) var(--transition-easing-ease-out);
  }

  .app__sidebar--mobile-open {
    left: 0;
  }

  .app__topbar {
    margin-left: 0;
  }

  .app__topbar--sidebar-collapsed {
    margin-left: 0;
  }

  .app__main {
    margin-left: 0;
    width: 100%;
  }

  .app__main--sidebar-collapsed {
    margin-left: 0;
  }
}
</style>
